import StrategyOverviewCard from "./StrategyOverviewCard";
import StrategiesCorrelationMatrix from "../correlationCard/StrategiesCorrelationMatrix";
import { useTranslation } from "@repo/core";
import type { PortfolioType } from "@repo/api";

interface StrategiesListProps {
  portfolios: PortfolioType[];
  isLoading?: boolean;
  className?: string;
}

/**
 * Component that renders the list of user strategies and correlation matrix
 */
export const StrategiesList = ({
  portfolios,
  isLoading = false,
  className = "",
}: StrategiesListProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={`space-y-8 ${className}`}>
        {/* Loading skeleton for strategy cards */}
        {[1, 2, 3].map((index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-48"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-64"></div>
              </div>
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
              ))}
            </div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (portfolios.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-gray-500">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("overview.noStrategies.title", "No portfolios yet")}
          </h3>
          <p className="text-gray-600">
            {t(
              "overview.noStrategies.description",
              "Create your first factor-based portfolio"
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-8 w-full max-w-full ${className}`} data-tour="overview-strategies">
      {/* Strategy Details Cards */}
      <div className="space-y-8 w-full">
        {portfolios.map((portfolio) => (
          <StrategyOverviewCard
            key={portfolio.modelId}
            portfolio={portfolio}
            className="w-full max-w-full"
          />
        ))}
      </div>

      {/* Correlation between Portfolios Section */}
      {portfolios.length > 1 && (
        <div className="mt-12 w-full max-w-full">
          <StrategiesCorrelationMatrix
            strategies={portfolios}
            className="w-full max-w-full"
          />
        </div>
      )}
    </div>
  );
};

export default StrategiesList;
